import { useEffect, useState } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from "@mui/material";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import { Mail, Save, Send } from "react-feather";
import { authData } from "../../components/GetAuth";
import styles from "./companyprofile.module.css";
import popupStyles from "../../components/popups/Popup.module.css";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const emptyValues = {
  mailjet_api_key: "",
  mailjet_secret_key: "",
  mailjet_from_address: "",
  mailjet_from_name: "",
};

const MailjetSettings = ({ settings, active, defaultTestEmail, onSaved, showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;

  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testError, setTestError] = useState("");
  const [testing, setTesting] = useState(false);

  const { handleSubmit, control, reset, getValues, trigger, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: emptyValues,
  });

  const hasSecretKey = !!settings?.has_secret_key;
  const isConfigured = !!settings?.mailjet_api_key && hasSecretKey && !!settings?.mailjet_from_address;

  useEffect(() => {
    if (settings) {
      reset({
        ...emptyValues,
        mailjet_api_key: settings.mailjet_api_key || "",
        mailjet_from_address: settings.mailjet_from_address || "",
        mailjet_from_name: settings.mailjet_from_name || "",
      });
    }
  }, [settings, reset]);

  const onSubmit = (data) => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/mailjet", data, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message, "success");
        onSaved(response.data.data);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      showMessage("Could not save Mailjet settings. Please try again.", "error");
    });
  };

  const openTest = async () => {
    const valid = await trigger();
    if (!valid) {
      showMessage("Please fix the highlighted Mailjet fields first.", "error");
      return;
    }
    setTestEmail(defaultTestEmail || "");
    setTestError("");
    setTestOpen(true);
  };

  const sendTest = (e) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(testEmail)) {
      setTestError("Enter a valid email address");
      return;
    }

    setTesting(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/mailjet/test", { ...getValues(), test_email: testEmail }, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setTesting(false);
      if (response.data.status === "SUCCESS") {
        setTestOpen(false);
        showMessage(response.data.message, "success");
      } else {
        setTestError(response.data.message);
      }
    }).catch(() => {
      setTesting(false);
      setTestError("Could not reach the server. Please try again.");
    });
  };

  return (<>
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <section className={`${styles.Card}`}>
        <div className={`${styles.CardHead}`}>
          <div>
            <h2 className={`${styles.CardTitle}`}><Mail />Mailjet configuration</h2>
            <p className={`${styles.CardSubtitle}`}>Send booking and customer emails through your Mailjet account. Find your API keys in Mailjet under Account settings &gt; API keys.</p>
          </div>
          <span className={`${styles.StatusPill} ${isConfigured && active ? styles.StatusOn : styles.StatusOff}`}>{!isConfigured ? "Not configured" : active ? "Active" : "Configured"}</span>
        </div>

        <div className={`${styles.CardBody}`}>
          <div className={`${styles.FormGrid}`}>
            <p className={`${styles.GroupLabel}`}>API credentials</p>

            <div>
              <Controller
                name="mailjet_api_key"
                control={control}
                rules={{ required: "API key is required" }}
                render={({ field }) => (<TextField {...field} label="API Key*" variant="outlined" fullWidth autoComplete="off" error={!!errors.mailjet_api_key} />)}
              />
              {errors.mailjet_api_key && (<span className={`${styles.FieldError}`}>{errors.mailjet_api_key.message}</span>)}
            </div>

            <div>
              <Controller
                name="mailjet_secret_key"
                control={control}
                rules={{ required: hasSecretKey ? false : "Secret key is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={hasSecretKey ? "Secret Key" : "Secret Key*"}
                    placeholder={hasSecretKey ? "••••••••" : ""}
                    InputLabelProps={hasSecretKey ? { shrink: true } : undefined}
                    variant="outlined"
                    type={showSecret ? "text" : "password"}
                    fullWidth
                    autoComplete="new-password"
                    error={!!errors.mailjet_secret_key}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowSecret((prev) => !prev)} edge="end" aria-label={showSecret ? "Hide secret key" : "Show secret key"}>
                            {showSecret ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              {errors.mailjet_secret_key
                ? <span className={`${styles.FieldError}`}>{errors.mailjet_secret_key.message}</span>
                : <span className={`${styles.FieldHint}`}>{hasSecretKey ? "A secret key is saved. Leave blank to keep it." : "Stored encrypted."}</span>}
            </div>

            <p className={`${styles.GroupLabel}`}>Sender</p>

            <div>
              <Controller
                name="mailjet_from_address"
                control={control}
                rules={{ required: "From email is required", pattern: { value: EMAIL_REGEX, message: "Invalid email address" } }}
                render={({ field }) => (<TextField {...field} label="From Email*" placeholder="bookings@yourcompany.com" variant="outlined" fullWidth error={!!errors.mailjet_from_address} />)}
              />
              {errors.mailjet_from_address
                ? <span className={`${styles.FieldError}`}>{errors.mailjet_from_address.message}</span>
                : <span className={`${styles.FieldHint}`}>Must be a verified sender in your Mailjet account.</span>}
            </div>

            <div>
              <Controller
                name="mailjet_from_name"
                control={control}
                render={({ field }) => (<TextField {...field} label="From Name" variant="outlined" fullWidth />)}
              />
            </div>
          </div>
        </div>

        <div className={`${styles.CardFooter}`}>
          <button type="button" className={`${styles.ActionBU} ${styles.ActionGhost}`} onClick={openTest} disabled={saving}><Send /> Send Test Email</button>
          <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={saving}><Save /> {saving ? "Saving..." : "Save & Use Mailjet"}</button>
        </div>
      </section>
    </form>

    <Dialog open={testOpen} onClose={() => !testing && setTestOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle className={`${popupStyles.PopupTitleBar}`}>
        Send test email
        <IconButton onClick={() => setTestOpen(false)} className={`${popupStyles.PopupCloseBU}`} aria-label="Close" disabled={testing}><Close /></IconButton>
      </DialogTitle>
      <DialogContent className={`${popupStyles.PopupContent}`}>
        <form onSubmit={sendTest} className={`${styles.DialogBody}`} noValidate>
          <p className={`${styles.CardSubtitle}`} style={{ marginBottom: 16 }}>We’ll send a test message using the settings in the form (they don’t need to be saved first).</p>
          <TextField label="Send to*" type="email" value={testEmail} onChange={(e) => { setTestEmail(e.target.value); setTestError(""); }} fullWidth autoFocus error={!!testError} />
          {testError && (<span className={`${styles.FieldError}`}>{testError}</span>)}
          <div className={`${styles.DialogActions}`}>
            <button type="button" className={`${styles.ActionBU} ${styles.ActionGhost}`} onClick={() => setTestOpen(false)} disabled={testing}>Cancel</button>
            <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={testing}><Send /> {testing ? "Sending..." : "Send"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  </>);
};

export default MailjetSettings;
